import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createPortal } from 'react-dom';
import { FaPlus, FaTimes } from 'react-icons/fa';
import { getProgressBillDetails, updateProgressBill } from '../../../../redux/Slices/ProgressBillSlice';
import { getTaxes, addTax } from '../../../../redux/Slices/authSlice';
import { useTranslation } from 'react-i18next';
import { makeOfflineRequest } from '../../../../shared/utils.js/offlineRequestManager';
import {
  isOnline,
  getAllProgressBillsCacheForQuery,
  saveDropdownCache,
  getDropdownCache,
  generateDropdownCacheKey,
  getAnyDropdownCache,
  getPendingRequests
} from '../../../../shared/utils.js/offlineQueue';

const normalizeListResponse = (payload) => {
  if (!payload) return { items: [], pagination: null };

  let items = [];
  let pagination = null;

  if (Array.isArray(payload?.data?.data)) {
    items = payload.data.data;
    pagination = payload.data?.meta || payload.data?.pagination || null;
  } else if (Array.isArray(payload?.data)) {
    items = payload.data;
    pagination = payload.meta || payload.pagination || null;
  } else if (Array.isArray(payload?.items)) {
    items = payload.items;
    pagination = payload.pagination || payload.meta || null;
  } else if (Array.isArray(payload)) {
    items = payload;
    pagination = null;
  } else {
    items = [];
    pagination = payload.pagination || payload.meta || null;
  }

  if (pagination) {
    pagination = {
      current_page: pagination.current_page || pagination.page || 1,
      last_page: pagination.last_page || pagination.total_pages || 1,
      per_page: pagination.per_page || 10,
      total: pagination.total || 0,
    };
  }

  return { items, pagination };
};

const EditProgressBill = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [billDetails, setBillDetails] = useState(null);
  const [selectedTaxes, setSelectedTaxes] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [billCategory, setBillCategory] = useState('');
  const [billType, setBillType] = useState('');
  const [billItems, setBillItems] = useState([]);


  // Tax dropdown
  const [taxDropdownOpen, setTaxDropdownOpen] = useState(false);
  const [taxSearchTerm, setTaxSearchTerm] = useState('');
  const [taxOptions, setTaxOptions] = useState([]);
  const [taxPage, setTaxPage] = useState(1);
  const [taxPagination, setTaxPagination] = useState(null);
  const [selectedTaxesData, setSelectedTaxesData] = useState([]); // Store selected taxes data
  const taxSearchInputRef = useRef(null);

  // Tax modal state
  const [showAddTaxModal, setShowAddTaxModal] = useState(false);
  const [taxForm, setTaxForm] = useState({
    name_ar: '',
    name_en: '',
    rate: '',
    is_active: true,
  });
  const [isSubmittingTax, setIsSubmittingTax] = useState(false);

  useEffect(() => {
    if (!id) {
      toast.error(t('progressBills.errors.fetchError'), { rtl: isRTL });
      navigate('/progress-bills');
      return;
    }

    const fetchDetails = async () => {
      try {
        setIsLoading(true);

        let details = null;

        // Try offline cache if offline
        if (!isOnline()) {
          try {
            // Try broad search with common page sizes to find the bill in cache
            const pageSizes = [10, 15, 20, 25, 50, 100];
            for (const pSize of pageSizes) {
              if (details) break;
              try {
                const cachedBills = await getAllProgressBillsCacheForQuery({ per_page: pSize });
                if (cachedBills && Array.isArray(cachedBills)) {
                  details = cachedBills.find((b) => String(b.id) === String(id));
                }
              } catch (e) { /* ignore */ }
            }

            // Check pending edits to reflect local changes
            try {
              const pendingReqs = await getPendingRequests();
              const pendingEdit = pendingReqs.find(req =>
                req.method === 'PUT' &&
                req.url.includes(`/contracts/progress-bills/${id}`)
              );

              if (pendingEdit) {
                const editData = pendingEdit.serializedData?.data || {};
                // If details found, merge edit data
                if (details) {
                  details = { ...details, ...editData };
                }
              }
            } catch (e) { console.error("Error reading pending requests", e); }

          } catch (cacheErr) {
            console.error("Error fetching from cache:", cacheErr);
          }
        }

        if (!details && isOnline()) {
          const payload = await dispatch(getProgressBillDetails({ id, language: i18n.language })).unwrap();
          details = payload?.data || payload;
        }

        if (!details) {
          throw new Error(isOnline() ? t('progressBills.errors.notFound') : t('progressBills.errors.offlineNotFound', { defaultValue: 'Bill not found in offline cache' }));
        }

        setBillDetails(details);
        setNotes(details?.notes || '');
        setInvoiceNumber(details?.invoice_number || '');
        setBillCategory(details?.bill_category || '');
        setBillType(details?.type || '');

        // Initialize taxes
        if (details?.taxes && Array.isArray(details.taxes)) {
          const firstTax = details.taxes[0];
          const taxIds = firstTax ? [String(firstTax.id || firstTax)] : [];
          setSelectedTaxes(taxIds);
          // Store full tax data
          setSelectedTaxesData((firstTax ? [firstTax] : []).filter(tax => tax.id || tax).map(tax => ({
            id: tax.id || tax,
            name_ar: tax.name_ar || tax.name,
            name_en: tax.name_en || tax.name,
          })));
        }

        // Initialize items
        if (details?.items && Array.isArray(details.items)) {
          setBillItems(details.items.map(item => ({
            ...item,
            id: item.id, // item id in progress_bill_items
            contract_item_id: item.contract_item_id,
            description: item.contract_item?.description_ar || item.contract_item?.description_en || item.description,
            unit: item.contract_item?.unit || item.unit,
            contract_qty: item.contract_item?.quantity || item.contract_item?.contract_qty || item.contract_qty,
            previous_qty: item.previous_qty,
            current_qty: item.current_qty || 0,
            total_executed: (parseFloat(item.previous_qty) || 0) + (parseFloat(item.current_qty) || 0),
            unit_price: item.unit_price || item.contract_item?.unit_price,
            total_amount: (parseFloat(item.current_qty) || 0) * (parseFloat(item.unit_price || item.contract_item?.unit_price) || 0),
          })));
        }


      } catch (error) {
        const errorMessage = typeof error === 'string'
          ? error
          : error?.message || error?.response?.data?.message || t('progressBills.errors.fetchError');

        if (!isOnline() && !errorMessage.includes('offline')) {
          toast.error(t('progressBills.errors.offlineLoadError', { defaultValue: "Could not load bill offline" }), { rtl: isRTL });
        } else {
          toast.error(errorMessage, { rtl: isRTL });
        }
        navigate('/progress-bills');
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetails();
  }, [dispatch, id, navigate, i18n.language, t, isRTL]);

  const fetchTaxes = useCallback(
    async (page = taxPage, search = taxSearchTerm) => {
      try {
        const queryParams = { page, per_page: 10, search: search || undefined };
        const cacheKey = generateDropdownCacheKey('taxes', queryParams);

        // Try cache first
        const cached = await getDropdownCache(cacheKey);
        if (cached) {
          setTaxOptions(cached.items);
          setTaxPagination(cached.pagination);
          if (!isOnline()) return;
        }

        if (!isOnline()) {
          const anyCached = await getAnyDropdownCache('taxes');
          if (anyCached && anyCached.length > 0) {
            setTaxOptions(anyCached);
          }
          return;
        }

        const payload = await dispatch(getTaxes(queryParams)).unwrap();
        const { items, pagination: paginationMeta } = normalizeListResponse(payload);

        await saveDropdownCache(cacheKey, items, paginationMeta);

        setTaxOptions(items);
        setTaxPagination(paginationMeta);
      } catch (error) {
        if (isOnline()) {
          toast.error(error || t('progressBills.errors.fetchTaxesError'), { rtl: isRTL });
        }
      }
    },
    [dispatch, isRTL, t]
  );

  useEffect(() => {
    if (taxDropdownOpen) {
      fetchTaxes(taxPage, taxSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxDropdownOpen, taxPage, taxSearchTerm]);

  useEffect(() => {
    if (taxSearchTerm !== undefined && taxDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setTaxPage(1);
        fetchTaxes(1, taxSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxSearchTerm, taxDropdownOpen]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      const isDropdownContainer = target.closest('[data-dropdown-container]');
      const isDropdownToggle = target.closest('[data-dropdown-toggle]');

      if (!isDropdownContainer && !isDropdownToggle) {
        if (taxDropdownOpen) setTaxDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [taxDropdownOpen]);


  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const mapped = files.map((file) => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      url: URL.createObjectURL(file),
    }));

    setAttachments((prev) => [...prev, ...mapped]);
    event.target.value = '';
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => {
      const target = prev.find((att) => att.id === id);
      if (target?.url) {
        URL.revokeObjectURL(target.url);
      }
      return prev.filter((att) => att.id !== id);
    });
  };

  const handleItemQuantityChange = (itemId, newQty) => {
    setBillItems(prevItems =>
      prevItems.map(item => {
        if (item.id === itemId) {
          const currentQty = parseFloat(newQty) || 0;
          const previousQty = parseFloat(item.previous_qty) || 0;
          const unitPrice = parseFloat(item.unit_price) || 0;

          return {
            ...item,
            current_qty: currentQty,
            total_executed: previousQty + currentQty,
            total_amount: currentQty * unitPrice,
          };
        }
        return item;
      })
    );
  };


  const getSelectedTaxes = () => {
    if (!selectedTaxes || selectedTaxes.length === 0) return [];

    // First, try to get from selectedTaxesData (preserved data)
    const fromPreserved = selectedTaxesData.filter((t) =>
      selectedTaxes.includes(String(t.id))
    );

    // Then, try to get from current taxOptions
    const fromOptions = taxOptions.filter((t) =>
      selectedTaxes.includes(String(t.id)) &&
      !fromPreserved.some(p => String(p.id) === String(t.id))
    );

    // Combine both sources
    const combined = [...fromPreserved, ...fromOptions];

    // If we still have missing taxes (IDs in selectedTaxes but not in combined),
    // create placeholder objects for them
    const missingIds = selectedTaxes.filter(id =>
      !combined.some(t => String(t.id) === String(id))
    );

    const placeholders = missingIds.map(id => ({
      id: id,
      name_ar: `${t('progressBills.editFields.tax')} #${id}`,
      name_en: `Tax #${id}`,
    }));

    return [...combined, ...placeholders];
  };

  const handleTaxFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTaxForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddTax = async (e) => {
    e.preventDefault();
    if (!taxForm.name_ar || !taxForm.name_en || taxForm.rate === '') {
      toast.error(t('quotations.add.modals.addTax.requiredFields', { defaultValue: 'برجاء استكمال الحقول المطلوبة' }), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingTax(true);
      const result = await dispatch(addTax({
        name_ar: taxForm.name_ar,
        name_en: taxForm.name_en,
        rate: Number(taxForm.rate),
        is_active: Boolean(taxForm.is_active),
      })).unwrap();
      if (result?.data?.id) {
        setSelectedTaxes([String(result.data.id)]);
        setSelectedTaxesData([{
          id: result.data.id,
          name_ar: taxForm.name_ar,
          name_en: taxForm.name_en,
        }]);
        // Refresh taxes list
        await fetchTaxes(taxPage, taxSearchTerm);
        setShowAddTaxModal(false);
        setTaxForm({
          name_ar: '',
          name_en: '',
          rate: '',
          is_active: true,
        });
        toast.success(t('quotations.add.modals.addTax.success', { defaultValue: 'تم إضافة الضريبة بنجاح' }), { rtl: isRTL });
      }
    } catch (error) {
      // Show detailed error from API
      let errorMessage = t('quotations.add.modals.addTax.error', { defaultValue: 'فشل إضافة الضريبة' });

      const errorData = error?.response?.data || error;

      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData?.errors) {
        const errorMessages = Object.values(errorData.errors).flat();
        errorMessage = errorMessages.join(', ');
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage, { rtl: isRTL });
    } finally {
      setIsSubmittingTax(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Get the ID from URL params or from billDetails as fallback
    const billId = id || billDetails?.id;

    if (!billId) {
      toast.error(t('progressBills.errors.fetchError'), { rtl: isRTL });
      return;
    }

    // Validate quantities
    const invalidItem = billItems.find(item => {
      const current = parseFloat(item.current_qty) || 0;
      const max = parseFloat(item.contract_qty) || 0; // In edit, we might compare with contract_qty or a remaining_qty if we have it
      // Note: for edit, it's safer to check if current_qty itself is valid.
      return current < 0;
    });

    if (invalidItem) {
      toast.error(
        isRTL
          ? `الكمية المدخلة للبند (${invalidItem.description}) غير صالحة`
          : `Invalid quantity for item (${invalidItem.description})`,
        { rtl: isRTL }
      );
      return;
    }


    try {
      setIsSubmitting(true);

      const formDataObj = new FormData();
      if (selectedTaxes && selectedTaxes.length > 0) {
        selectedTaxes.forEach(id => formDataObj.append('taxes[]', id));
      }
      if (notes) formDataObj.append('notes', notes);
      if (invoiceNumber) formDataObj.append('invoice_number', invoiceNumber);
      if (billCategory) formDataObj.append('bill_category', billCategory);
      if (billType) formDataObj.append('type', billType);

      if (attachments.length > 0) {
        attachments.forEach(att => formDataObj.append('attachments[]', att.file));
      }

      // Add items to formData for offline support
      if (billItems && billItems.length > 0) {
        billItems.forEach((item, index) => {
          formDataObj.append(`items[${index}][id]`, item.id);
          formDataObj.append(`items[${index}][current_qty]`, item.current_qty);
          formDataObj.append(`items[${index}][previous_qty]`, item.previous_qty || 0);
        });
      }


      // Add _method: PUT for Laravel multipart/form-data support (required for file uploads on updates)
      formDataObj.append('_method', 'PUT');

      // If offline
      if (!isOnline()) {
        // Use POST with _method: PUT to the correct endpoint (ProgressBillSlice uses /progress-bills/:id)
        await makeOfflineRequest(`/progress-bills/${billId}`, 'POST', formDataObj);

        toast.info(
          isRTL
            ? 'تم حفظ التعديلات محلياً وسيتم إرسالها عند عودة الاتصال'
            : 'Changes saved locally and will be sent when online',
          { rtl: isRTL, autoClose: 5000 }
        );
        navigate('/progress-bills');
        return;
      }

      const progressBillData = {
        taxes: selectedTaxes && selectedTaxes.length > 0 ? selectedTaxes.map(taxId => Number(taxId)) : undefined,
        notes: notes || undefined,
        invoice_number: invoiceNumber || undefined,
        attachments: attachments.length > 0 ? attachments.map(att => att.file) : undefined,
        bill_category: billCategory || undefined,
        type: billType || undefined,
        contract_id: billDetails?.contract_id || undefined,
        project_id: billDetails?.project_id || undefined,
        items: billItems.map(item => ({
          id: item.id,
          current_qty: item.current_qty,
          previous_qty: item.previous_qty || 0
        })),
      };

      await dispatch(updateProgressBill({ id: billId, progressBillData })).unwrap();

      toast.success(t('progressBills.success.updated'), { rtl: isRTL });
      navigate('/progress-bills');
    } catch (error) {
      if (error.message === 'OFFLINE_REQUEST_SAVED') {
        toast.info(
          isRTL
            ? 'تم حفظ التعديلات محلياً وسيتم إرسالها عند عودة الاتصال'
            : 'Changes saved locally and will be sent when online',
          { rtl: isRTL, autoClose: 5000 }
        );
        navigate('/progress-bills');
        return;
      }
      const errorData = error?.response?.data || error;
      const errorMessage = typeof errorData === 'string'
        ? errorData
        : errorData?.errors
          ? Object.values(errorData.errors).flat().join(', ')
          : errorData?.message || error?.message || t('progressBills.errors.updateError');
      toast.error(errorMessage, { rtl: isRTL });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', padding: 40, color: '#ccc' }}>{t('progressBills.loading')}</div>
        </div>
      </div>
    );
  }

  const contractCurrencyName = (
    billDetails?.contract?.currency_name ||
    billDetails?.contract?.currency?.name_ar ||
    billDetails?.contract?.currency?.name_en ||
    billDetails?.contract?.currency?.code ||
    billDetails?.contract?.quotation?.currency_name ||
    billDetails?.contract?.quotation?.currency?.name_ar ||
    billDetails?.contract?.quotation?.currency?.name_en ||
    billDetails?.contract?.quotation?.currency?.code ||
    ''
  );

  const contractExchangeRate = (
    billDetails?.contract?.exchange_rate ??
    billDetails?.contract?.currency?.exchange_rate ??
    billDetails?.contract?.quotation?.exchange_rate ??
    billDetails?.exchange_rate ??
    ''
  );

  return (
    <div style={{ ...pageStyle, direction: dir, textAlign }}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
          <h2 style={{ margin: 0, color: 'white', fontSize: '18px' }}>
            {t('progressBills.editTitle')} {billDetails?.bill_no || `#${id}`}
          </h2>
          <button type="button" onClick={() => navigate('/progress-bills')} style={{ backgroundColor: '#666', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 18px', cursor: 'pointer', fontSize: '14px' }}>
            {t('btns.back', { defaultValue: 'Back' })}
          </button>
        </div>

        {billDetails && (
          <div style={{ marginBottom: 24, padding: 16, backgroundColor: '#1a1f2e', borderRadius: 8, border: '1px solid #333' }}>
            <div style={infoGridStyle}>
              <div>
                <span style={{ color: '#999', fontSize: 12 }}>{t('progressBills.editFields.project')}:</span>
                <div style={{ color: 'white', marginTop: 4 }}>
                  {billDetails.project?.name_ar || billDetails.project?.name_en || '—'}
                </div>
              </div>
              <div>
                <span style={{ color: '#999', fontSize: 12 }}>{t('progressBills.editFields.contract')}:</span>
                <div style={{ color: 'white', marginTop: 4 }}>
                  {billDetails.contract?.code || billDetails.contract_id || '—'}
                </div>
              </div>
              <div>
                <span style={{ color: '#999', fontSize: 12 }}>{t('progressBills.editFields.currencyName', { defaultValue: 'العملة' })}:</span>
                <div style={{ marginTop: 4 }}>
                  <input
                    value={contractCurrencyName}
                    disabled
                    style={{
                      width: '100%',
                      padding: 8,
                      borderRadius: 6,
                      border: '1px solid #333',
                      backgroundColor: '#1a1f2e',
                      color: 'white',
                      fontSize: 13,
                      textAlign: isRTL ? 'right' : 'left',
                      direction: isRTL ? 'rtl' : 'ltr',
                    }}
                  />
                </div>
              </div>
              <div>
                <span style={{ color: '#999', fontSize: 12 }}>{t('progressBills.editFields.exchangeRate', { defaultValue: 'سعر الصرف' })}:</span>
                <div style={{ marginTop: 4 }}>
                  <input
                    value={contractExchangeRate}
                    disabled
                    style={{
                      width: '100%',
                      padding: 8,
                      borderRadius: 6,
                      border: '1px solid #333',
                      backgroundColor: '#1a1f2e',
                      color: 'white',
                      fontSize: 13,
                      textAlign: isRTL ? 'right' : 'left',
                      direction: isRTL ? 'rtl' : 'ltr',
                    }}
                  />
                </div>
              </div>
              <div>
                <span style={{ color: '#999', fontSize: 12 }}>{t('progressBills.tableHeaders.from')}:</span>
                <div style={{ color: 'white', marginTop: 4 }}>
                  {billDetails.period_start ? new Date(billDetails.period_start).toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' }) : '—'}
                </div>
              </div>
              <div>
                <span style={{ color: '#999', fontSize: 12 }}>{t('progressBills.tableHeaders.to')}:</span>
                <div style={{ color: 'white', marginTop: 4 }}>
                  {billDetails.period_end ? new Date(billDetails.period_end).toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' }) : '—'}
                </div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 14 }}>{t('progressBills.editFields.billCategory', { defaultValue: 'فئة المستخلص' })}</span>
              <select
                value={billCategory}
                onChange={(e) => setBillCategory(e.target.value)}
                style={{
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid #333',
                  backgroundColor: '#1a1f2e',
                  color: 'white',
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                <option value="">{t('progressBills.addFields.selectBill')}</option>
                <option value="running">{t('progressBills.addFields.billCategoryRunning')}</option>
                <option value="final">{t('progressBills.editFields.billCategoryFinal', { defaultValue: 'ختامي' })}</option>
              </select>
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 14 }}>{t('progressBills.editFields.billType', { defaultValue: 'نوع المستخلص' })}</span>
              <select
                value={billType}
                onChange={(e) => setBillType(e.target.value)}
                style={{
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid #333',
                  backgroundColor: '#1a1f2e',
                  color: 'white',
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                <option value="">{t('progressBills.addFields.selectBillType')}</option>
                <option value="bill">{t('progressBills.editFields.typeBill')}</option>
                <option value="claim">{t('progressBills.editFields.typeClaim')}</option>
              </select>
            </label>
          </div>

          {/* Items Table */}
          {billItems.length > 0 && (
            <div style={{ backgroundColor: '#1b3350', border: '1px solid #333', borderRadius: 12, overflow: 'hidden', marginTop: 16 }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #333', backgroundColor: '#1a1f2e' }}>
                <h3 style={{ margin: 0, color: 'white', fontSize: 16 }}>
                  {t('progressBills.viewFields.items', { defaultValue: 'بنود المستخلص' })}
                </h3>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                  <thead>
                    <tr style={{ backgroundColor: '#AC2000' }}>
                      <th style={{ padding: 14, textAlign: 'center', color: 'white', fontWeight: 'bold', fontSize: 14, borderBottom: '1px solid #333' }}>
                        {t('progressBills.table.description', { defaultValue: 'الوصف' })}
                      </th>
                      <th style={{ padding: 14, textAlign: 'center', color: 'white', fontWeight: 'bold', fontSize: 14, borderBottom: '1px solid #333' }}>
                        {t('progressBills.table.unit', { defaultValue: 'الوحدة' })}
                      </th>
                      <th style={{ padding: 14, textAlign: 'center', color: 'white', fontWeight: 'bold', fontSize: 14, borderBottom: '1px solid #333' }}>
                        {t('progressBills.table.contractQty', { defaultValue: 'كمية العقد' })}
                      </th>
                      <th style={{ padding: 14, textAlign: 'center', color: 'white', fontWeight: 'bold', fontSize: 14, borderBottom: '1px solid #333' }}>
                        {t('progressBills.table.previousQty', { defaultValue: 'ما سبق تنفيذه' })}
                      </th>
                      <th style={{ padding: 14, textAlign: 'center', color: 'white', fontWeight: 'bold', fontSize: 14, borderBottom: '1px solid #333' }}>
                        {t('progressBills.table.currentQty', { defaultValue: 'الكمية الحالية' })}
                      </th>
                      <th style={{ padding: 14, textAlign: 'center', color: 'white', fontWeight: 'bold', fontSize: 14, borderBottom: '1px solid #333' }}>
                        {t('progressBills.table.totalExecuted', { defaultValue: 'إجمالي المنفذ' })}
                      </th>
                      <th style={{ padding: 14, textAlign: 'center', color: 'white', fontWeight: 'bold', fontSize: 14, borderBottom: '1px solid #333' }}>
                        {t('progressBills.table.unitPrice', { defaultValue: 'سعر الوحدة' })}
                      </th>
                      <th style={{ padding: 14, textAlign: 'center', color: 'white', fontWeight: 'bold', fontSize: 14, borderBottom: '1px solid #333' }}>
                        {t('progressBills.table.totalAmount', { defaultValue: 'الإجمالي' })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {billItems.map((item, index) => (
                      <tr key={item.id || index} style={{ borderBottom: '1px solid #333', backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.02)' }}>
                        <td style={{ padding: 14, textAlign: 'center', color: '#ddd', fontSize: 13 }}>
                          {item.description || '—'}
                        </td>
                        <td style={{ padding: 14, textAlign: 'center', color: '#ddd', fontSize: 13 }}>
                          {item.unit || '—'}
                        </td>
                        <td style={{ padding: 14, textAlign: 'center', color: '#ddd', fontSize: 13 }}>
                          {item.contract_qty ? parseFloat(item.contract_qty).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                        </td>
                        <td style={{ padding: 14, textAlign: 'center', color: '#888', fontSize: 13, fontStyle: 'italic' }}>
                          {item.previous_qty ? parseFloat(item.previous_qty).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                        </td>
                        <td style={{ padding: 14, textAlign: 'center' }}>
                          <input
                            type="number"
                            min="0"
                            
                            value={item.current_qty || 0}
                            onChange={(e) => handleItemQuantityChange(item.id, e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px',
                              backgroundColor: '#1a1f2e',
                              border: '1px solid #333',
                              borderRadius: 6,
                              color: 'white',
                              fontSize: 13,
                              textAlign: 'center',
                              direction: dir
                            }}
                          />
                        </td>
                        <td style={{ padding: 14, textAlign: 'center', color: '#4CAF50', fontSize: 13, fontWeight: 'bold' }}>
                          {item.total_executed ? parseFloat(item.total_executed).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                        </td>
                        <td style={{ padding: 14, textAlign: 'center', color: '#ddd', fontSize: 13 }}>
                          {item.unit_price ? parseFloat(item.unit_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                        </td>
                        <td style={{ padding: 14, textAlign: 'center', color: '#FFA726', fontSize: 13, fontWeight: 'bold' }}>
                          {item.total_amount ? parseFloat(item.total_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ position: 'relative' }} data-dropdown-container>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 14 }}>{t('progressBills.editFields.taxes')}</span>
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); setShowAddTaxModal(true); }}
                  style={{ backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <FaPlus size={10} />
                  {t('progressBills.editFields.addTax', { defaultValue: 'إضافة ضريبة' })}
                </button>
              </div>
              <div style={{ position: 'relative' }}>
                <div
                  data-dropdown-toggle
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setTaxDropdownOpen((prev) => !prev);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  style={{ ...dropdownDisplayStyle, flexWrap: 'wrap', minHeight: 44, alignItems: 'center' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, flex: 1 }}>
                    {getSelectedTaxes().length > 0 ? (
                      getSelectedTaxes().map((tax) => (
                        <span
                          key={tax.id}
                          style={{ backgroundColor: '#AC2000', color: 'white', padding: '3px 8px', borderRadius: 4, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
                        >
                          {isRTL ? (tax.name_ar || tax.name_en) : (tax.name_en || tax.name_ar)}
                          <button
                            type="button"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              const removedId = String(tax.id);
                              setSelectedTaxes((prev) => prev.filter((id) => String(id) !== removedId));
                              setSelectedTaxesData((prev) => prev.filter((t) => String(t.id) !== removedId));
                            }}
                            style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1 }}
                          >×</button>
                        </span>
                      ))
                    ) : (
                      <span style={{ color: '#888' }}>{t('progressBills.editFields.selectTaxes')}</span>
                    )}
                  </div>
                  <span style={{ fontSize: 12 }}>▼</span>
                </div>
              </div>
              <DropdownWithSearchSingle
                isOpen={taxDropdownOpen}
                searchTerm={taxSearchTerm}
                onSearchChange={setTaxSearchTerm}
                searchInputRef={taxSearchInputRef}
                items={taxOptions}
                pagination={taxPagination}
                onPageChange={setTaxPage}
                onClose={() => setTaxDropdownOpen(false)}
                onSelect={(item) => {
                  const itemId = String(item.id);
                  setSelectedTaxes((prev) => (prev.includes(itemId) ? prev : [...prev, itemId]));
                  setSelectedTaxesData((prev) => (prev.some((t) => String(t.id) === itemId) ? prev : [...prev, item]));
                  setTaxDropdownOpen(false);
                  setTaxSearchTerm('');
                  setTaxPage(1);
                }}
                selectedId={selectedTaxes?.[0] || ''}
                label={t('progressBills.editFields.tax')}
                t={t}
                isRTL={isRTL}
              />
            </div>
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 14 }}>{t('progressBills.editFields.invoiceNumber', { defaultValue: 'رقم الفاتورة' })}</span>
            <input
              type="text"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder={t('progressBills.editFields.invoiceNumberPlaceholder', { defaultValue: 'أدخل رقم الفاتورة' })}
              style={{
                padding: 12,
                borderRadius: 8,
                border: '1px solid #333',
                backgroundColor: '#1a1f2e',
                color: 'white',
                fontSize: 14,
              }}
            />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span>{t('progressBills.editFields.attachments')}</span>
            <input
              type="file"
              multiple
              onChange={handleFileUpload}
              style={{
                padding: 12,
                borderRadius: 8,
                border: '1px solid #333',
                backgroundColor: '#1a1f2e',
                color: 'white',
              }}
            />
            {attachments.length > 0 && (
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {attachments.map((attachment) => (
                  <span
                    key={attachment.id}
                    style={{
                      backgroundColor: '#AC2000',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: 4,
                      fontSize: 12,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    {attachment.name}
                    <button
                      type="button"
                      onClick={() => removeAttachment(attachment.id)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: 14,
                        padding: 0,
                        marginLeft: 4,
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span>{t('progressBills.editFields.notes')}</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              style={{
                padding: 12,
                borderRadius: 8,
                border: '1px solid #333',
                backgroundColor: '#1a1f2e',
                color: 'white',
                resize: 'vertical',
              }}
            />
          </label>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button type="submit" style={submitButtonStyle} disabled={isSubmitting}>
              {isSubmitting ? t('progressBills.editFields.saving') : t('progressBills.editFields.save')}
            </button>
            <button type="button" style={secondaryButtonStyle} onClick={() => navigate('/progress-bills')}>
              {t('progressBills.editFields.cancel')}
            </button>
          </div>
        </form>
      </div>

      {/* Modal لإضافة ضريبة جديدة */}
      {showAddTaxModal && createPortal(
        <div
          data-modal-overlay
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
            padding: '20px'
          }}
          onClick={() => setShowAddTaxModal(false)}
        >
          <div
            data-modal-content
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addTax.title', { defaultValue: 'إضافة ضريبة جديدة' })}</h2>
              <button
                onClick={() => {
                  setShowAddTaxModal(false);
                  setTaxForm({
                    name_ar: '',
                    name_en: '',
                    rate: '',
                    is_active: true,
                  });
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddTax}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addTax.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span></label>
                  <input type="text" name="name_ar" value={taxForm.name_ar} onChange={handleTaxFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} required style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addTax.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span></label>
                  <input type="text" name="name_en" value={taxForm.name_en} onChange={handleTaxFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} required style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addTax.rate', { defaultValue: 'النسبة' })} <span style={{ color: '#dc3545' }}>*</span></label>
                  <input type="number"  name="rate" value={taxForm.rate} onChange={handleTaxFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} required style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addTax.isActive', { defaultValue: 'نشط' })}</label>
                  <select name="is_active" value={taxForm.is_active ? 'true' : 'false'} onChange={handleTaxFormChange} onFocus={(e) => e.stopPropagation()} onBlur={(e) => e.stopPropagation()} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    <option value="true">{t('quotations.add.modals.addTax.active', { defaultValue: 'نشط' })}</option>
                    <option value="false">{t('quotations.add.modals.addTax.inactive', { defaultValue: 'غير نشط' })}</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
                <button
                  type="submit"
                  disabled={isSubmittingTax}
                  style={{
                    backgroundColor: isSubmittingTax ? '#666' : '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: isSubmittingTax ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    opacity: isSubmittingTax ? 0.6 : 1
                  }}
                >
                  {isSubmittingTax ? t('progressBills.addFields.saving', { defaultValue: 'جاري الحفظ...' }) : t('progressBills.addFields.save', { defaultValue: 'حفظ' })}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddTaxModal(false);
                    setTaxForm({
                      name_ar: '',
                      name_en: '',
                      rate: '',
                      is_active: true,
                    });
                  }}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  {t('progressBills.addFields.cancel', { defaultValue: 'إلغاء' })}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const pageStyle = {
  padding: 30,
  backgroundColor: '#14263b',
  minHeight: 'calc(100vh - 80px)',
  color: 'white',
  display: 'flex',
  justifyContent: 'center',
};

const cardStyle = {
  width: '100%',
  maxWidth: 1200,
  backgroundColor: '#1b3350',
  borderRadius: 12,
  padding: 24,
  border: '1px solid #333',
};

const infoGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 16,
};

const submitButtonStyle = {
  backgroundColor: '#AC2000',
  color: 'white',
  border: 'none',
  padding: '12px 18px',
  borderRadius: 8,
  cursor: 'pointer',
  minWidth: 120,
};

const secondaryButtonStyle = {
  backgroundColor: 'transparent',
  color: 'white',
  border: '1px solid #444',
  padding: '12px 18px',
  borderRadius: 8,
  cursor: 'pointer',
  minWidth: 120,
};

const dropdownDisplayStyle = {
  width: '100%',
  padding: '12px 14px',
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  color: 'white',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  textAlign: 'right',
};

const dropdownMenuStyle = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  zIndex: 20,
  padding: 12,
  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
};

const dropdownSearchStyle = {
  width: '100%',
  padding: 10,
  borderRadius: 6,
  border: '1px solid #333',
  backgroundColor: '#14263b',
  color: 'white',
  marginBottom: 10,
};

const dropdownEmptyStyle = {
  padding: 12,
  textAlign: 'center',
  color: '#ccc',
  fontSize: 13,
};

const dropdownPaginationStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 10,
};

const DropdownWithSearch = ({
  isOpen,
  searchTerm,
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  onPageChange,
  onClose,
  onSelect,
  label,
  renderItem,
  t,
  dropdownItemStyle = {},
}) => {
  if (!isOpen) return null;

  // Custom render for entities
  const formatEntity = renderItem || ((entity) => {
    if (!entity) return '—';
    if (entity.quotation_no) {
      let text = `${entity.quotation_no}`;
      if (entity.subject) text += ` - ${entity.subject}`;
      if (entity.project) {
        text += ` - ${entity.project.name_ar || entity.project.name_en || ''}`;
      }
      return text;
    }
    const code = entity.code ? `${entity.code} - ` : '';
    const name = entity.name_ar || entity.name_en || '';
    return `${code}${name}` || `#${entity.id}`;
  });

  return (
    <div style={dropdownMenuStyle} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
      <input
        ref={searchInputRef}
        type="text"
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={t('progressBills.searchPlaceholderDropdown', { label })}
        style={dropdownSearchStyle}
        onClick={(e) => e.stopPropagation()}
      />
      {pagination && pagination.last_page > 0 && (
        <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#ccc', fontSize: 11 }}>{t('progressBills.pagination.page')} {pagination.current_page} {t('progressBills.pagination.of')} {pagination.last_page}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>««</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>‹</button>
            {(() => {
              const pages = [];
              const current = pagination.current_page;
              const last = pagination.last_page;
              const showPages = 3;
              let start = Math.max(1, current - 1);
              let end = Math.min(last, start + showPages - 1);
              if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);

              for (let i = start; i <= end; i++) {
                pages.push(i);
              }

              return pages.map(pageNum => (
                <button key={pageNum} type="button" onClick={(e) => { e.stopPropagation(); onPageChange(pageNum); }} style={{ padding: '4px 8px', backgroundColor: pageNum === pagination.current_page ? '#AC2000' : '#1a1f2e', color: 'white', border: '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>
                  {pageNum}
                </button>
              ));
            })()}
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>›</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>»»</button>
          </div>
        </div>
      )}
      <div style={{ maxHeight: 300, overflowY: 'auto', paddingBottom: 4 }}>
        {(Array.isArray(items) ? items : []).length === 0 ? (
          <div style={dropdownEmptyStyle}>{t('progressBills.noResults')}</div>
        ) : (
          (Array.isArray(items) ? items : []).map((item) => (
            <div
              key={item.id}
              style={{ padding: '10px 12px', borderTop: '1px solid #333', cursor: 'pointer', ...dropdownItemStyle }}
              onClick={() => {
                onSelect(item);
                onClose?.();
              }}
            >
              {formatEntity(item)}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const DropdownWithSearchSingle = ({
  isOpen,
  searchTerm,
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  onPageChange,
  onSelect,
  onClose,
  label,
  selectedId = '',
  t,
  isRTL,
}) => {
  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, items, searchInputRef]);

  if (!isOpen) return null;

  const isSelected = (itemId) => String(selectedId) === String(itemId);

  const formatEntity = (entity) => {
    if (!entity) return '—';
    return isRTL
      ? (entity.name_ar || entity.name_en || `#${entity.id}`)
      : (entity.name_en || entity.name_ar || `#${entity.id}`);
  };

  return (
    <div style={dropdownMenuStyle} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
      <div style={{ padding: 8 }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={t('progressBills.searchPlaceholderDropdown', { label })}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 0);
          }}
          onFocus={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          autoFocus
          style={dropdownSearchStyle}
        />
      </div>
      {pagination && (pagination.last_page > 1 || (pagination.last_page === 1 && pagination.current_page === 1)) && (
        <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#ccc', fontSize: 11 }}>{t('progressBills.pagination.page')} {pagination.current_page} {t('progressBills.pagination.of')} {pagination.last_page}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>««</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>‹</button>
            {(() => {
              const pages = [];
              const current = pagination.current_page;
              const last = pagination.last_page;
              const showPages = 3;
              let start = Math.max(1, current - 1);
              let end = Math.min(last, start + showPages - 1);
              if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);

              for (let i = start; i <= end; i++) {
                pages.push(i);
              }

              return pages.map(pageNum => (
                <button key={pageNum} type="button" onClick={(e) => { e.stopPropagation(); onPageChange(pageNum); }} style={{ padding: '4px 8px', backgroundColor: pageNum === pagination.current_page ? '#AC2000' : '#1a1f2e', color: 'white', border: '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>
                  {pageNum}
                </button>
              ));
            })()}
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>›</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>»»</button>
          </div>
        </div>
      )}
      <div style={{ maxHeight: 300, overflowY: 'auto', paddingBottom: 4 }}>
        {(Array.isArray(items) ? items : []).length === 0 ? (
          <div style={dropdownEmptyStyle}>{t('progressBills.noResults')}</div>
        ) : (
          (Array.isArray(items) ? items : []).map((item, index) => {
            const selected = isSelected(item.id);
            return (
              <div
                key={item.id}
                style={{
                  padding: '10px 12px',
                  borderTop: index === 0 ? 'none' : '1px solid #333',
                  cursor: 'pointer',
                  backgroundColor: selected ? '#2a2a2a' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSelect(item);
                  onClose?.();
                }}
              >
                <input
                  type="radio"
                  checked={selected}
                  readOnly
                  style={{ cursor: 'pointer', flexShrink: 0, pointerEvents: 'none' }}
                />
                <span
                  style={{
                    flex: 1,
                    cursor: 'pointer',
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    overflow: 'visible',
                    textOverflow: 'clip',
                    userSelect: 'none',
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  {formatEntity(item)}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default EditProgressBill;
